import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CaretDown } from "@phosphor-icons/react";
import { RatingBadge } from "../shared/RatingBadge";
import { TeamLogo } from "../shared/TeamLogo";

const STATUS_LABEL = {
  scheduled: "Scheduled",
  in_progress: "In Progress",
  final: "Final",
  postponed: "Postponed",
  canceled: "Canceled",
};

const SEGMENT_SHORT = {
  FULL_GAME: "F",
  FIRST_FIVE: "F5",
  FIRST_INNING: "1st",
};

export const GameCard = ({ game, segment = "FULL_GAME", prediction = null }) => {
  const awayName = game.away_team?.name || game.away_team_id || "TBD";
  const homeName = game.home_team?.name || game.home_team_id || "TBD";

  const segmentScore = game.segment_scores?.[segment];
  const hasSegmentScore = Boolean(segmentScore);
  const isScored = game.status === "final" || game.status === "in_progress";

  // O/U preview: the real synced sportsbook total when one exists, otherwise EdgeBall's own
  // projected total (never fabricated) -- full breakdown lives on the game detail page.
  // `prediction` is fetched once by Dashboard for all of today's games (also feeds Best Bets).
  const total = prediction?.total || null;
  const ouIsMarket = Boolean(total?.market_line);
  const ouValue = ouIsMarket ? total.market_line.line_value : total?.status === "ok" ? total.projected_total.toFixed(1) : null;

  // ML preview: the existing win-probability prediction, shown away/home to match the
  // away-@-home team order above -- full breakdown lives on the game detail Prediction card.
  const hasMlProbabilities = prediction?.status === "ok" && prediction.away_probability != null && prediction.home_probability != null;
  const mlValue = hasMlProbabilities ? `${(prediction.away_probability * 100).toFixed(0)}/${(prediction.home_probability * 100).toFixed(0)}` : null;

  // Green side-line: a strong O/U lean (either side's own model probability >= 58%) surfaced
  // at a glance without opening the game -- same probabilities already shown on the game
  // detail Prediction card, never a separately-fabricated number.
  const ouRating =
    total?.status === "ok" && total.over_probability != null && total.under_probability != null
      ? Math.max(total.over_probability, total.under_probability)
      : null;
  const ouHighlighted = ouRating !== null && ouRating >= 0.58;

  // First 5 O/U (yellow) marker below: a strong First 5 O/U model lean (either side's own
  // probability >= 58%), same pattern as the green Full Game O/U line above.
  const f5Total = prediction?.f5_total || null;
  const f5Rating =
    f5Total?.status === "ok" && f5Total.over_probability != null && f5Total.under_probability != null
      ? Math.max(f5Total.over_probability, f5Total.under_probability)
      : null;
  const f5Highlighted = f5Rating !== null && f5Rating >= 0.58;

  // NRFI (red) / YRFI (blue) markers: NRFI+YRFI always sum to 1.0, so only one of the two can
  // ever cross 58% for a given game -- same raw-model-probability convention as O/U and F5
  // above (no market comparison involved).
  const nrfiYrfi = prediction?.nrfi_yrfi || null;
  const nrfiProbability = nrfiYrfi?.status === "ok" ? nrfiYrfi.nrfi_probability : null;
  const yrfiProbability = nrfiYrfi?.status === "ok" ? nrfiYrfi.yrfi_probability : null;
  const nrfiHighlighted = nrfiProbability !== null && nrfiProbability >= 0.58;
  const yrfiHighlighted = yrfiProbability !== null && yrfiProbability >= 0.58;

  // Market Preview dropdown near "Edge --": a quick look at every market's own prediction.
  // Only Full Game O/U has a real market-based edge computed anywhere in this app today, so
  // every other row honestly reports "N/A" rather than fabricating a number.
  const [showPreview, setShowPreview] = useState(false);
  const previewRef = useRef(null);
  useEffect(() => {
    if (!showPreview) return;
    const handleClickOutside = (e) => {
      if (previewRef.current && !previewRef.current.contains(e.target)) setShowPreview(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPreview]);

  const ouEdge = total?.edge || null;
  const ouPreviewSide = total?.status === "ok" && total.over_probability != null && total.under_probability != null ? (total.over_probability >= total.under_probability ? "Over" : "Under") : null;
  const ouPreviewProb = ouPreviewSide ? Math.max(total.over_probability, total.under_probability) : null;
  const ouPreviewEdgePct = ouEdge?.recommended_side ? (ouEdge.recommended_side === "over" ? ouEdge.over_edge_percentage : ouEdge.under_edge_percentage) : null;

  const f5PreviewSide = f5Total?.status === "ok" && f5Total.over_probability != null && f5Total.under_probability != null ? (f5Total.over_probability >= f5Total.under_probability ? "Over" : "Under") : null;

  // NRFI/YRFI edge (once a first-inning market line is synced) uses the exact same no-vig
  // technique as Full Game O/U's own edge block -- see nrfi_yrfi.edge on the backend. Both
  // edge fields are raw probability-point fractions (e.g. 0.093 == 9.3 percentage points).
  const nrfiYrfiEdge = nrfiYrfi?.edge || null;
  const formatEdge = (pct) => (pct !== null && pct !== undefined ? `${pct > 0 ? "+" : ""}${(pct * 100).toFixed(1)}%` : "N/A");

  const previewRows = [
    { key: "ml", label: "ML", value: hasMlProbabilities ? `${awayName} ${(prediction.away_probability * 100).toFixed(0)}% / ${homeName} ${(prediction.home_probability * 100).toFixed(0)}%` : "--", edge: "N/A" },
    { key: "ou", label: "O/U", value: ouPreviewProb !== null ? `${ouPreviewSide} ${(ouPreviewProb * 100).toFixed(0)}%` : "--", edge: formatEdge(ouPreviewEdgePct) },
    { key: "f5-ou", label: "First 5 O/U", value: f5Rating !== null ? `${f5PreviewSide} ${(f5Rating * 100).toFixed(0)}%` : "--", edge: "N/A" },
    { key: "yrfi", label: "YRFI", value: yrfiProbability !== null ? `${(yrfiProbability * 100).toFixed(0)}%` : "--", edge: formatEdge(nrfiYrfiEdge?.yrfi_edge_percentage) },
    { key: "nrfi", label: "NRFI", value: nrfiProbability !== null ? `${(nrfiProbability * 100).toFixed(0)}%` : "--", edge: formatEdge(nrfiYrfiEdge?.nrfi_edge_percentage) },
  ];

  return (
    <Link
      to={`/games/${game.id}`}
      data-testid={`game-card-${game.id}`}
      className="group relative grid grid-cols-1 gap-4 rounded-lg border border-white/10 bg-eb-surface p-4 pl-6 transition-all hover:-translate-y-0.5 hover:border-white/20 sm:grid-cols-3"
    >
      {(ouHighlighted || f5Highlighted || nrfiHighlighted || yrfiHighlighted) && (
        <span className="absolute bottom-2 left-1.5 top-2 flex gap-0.5">
          {ouHighlighted && (
            <span
              data-testid={`game-card-ou-highlight-${game.id}`}
              title={`Strong O/U model lean \u2014 ${(ouRating * 100).toFixed(0)}%`}
              className="h-full w-1 rounded-full bg-green-500"
            />
          )}
          {f5Highlighted && (
            <span
              data-testid={`game-card-f5-highlight-${game.id}`}
              title={`Strong First 5 O/U model lean \u2014 ${(f5Rating * 100).toFixed(0)}%`}
              className="h-full w-1 rounded-full bg-yellow-400"
            />
          )}
          {nrfiHighlighted && (
            <span
              data-testid={`game-card-nrfi-highlight-${game.id}`}
              title={`Strong NRFI model lean \u2014 ${(nrfiProbability * 100).toFixed(0)}%`}
              className="h-full w-1 rounded-full bg-red-500"
            />
          )}
          {yrfiHighlighted && (
            <span
              data-testid={`game-card-yrfi-highlight-${game.id}`}
              title={`Strong YRFI model lean \u2014 ${(yrfiProbability * 100).toFixed(0)}%`}
              className="h-full w-1 rounded-full bg-blue-500"
            />
          )}
        </span>
      )}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5 font-heading text-lg text-white">
          <TeamLogo team={game.away_team} size={24} testId={`game-card-logo-away-${game.id}`} />
          <span>{awayName}</span>
          <span className="text-sm text-zinc-500">@</span>
          <TeamLogo team={game.home_team} size={24} testId={`game-card-logo-home-${game.id}`} />
          <span>{homeName}</span>
        </div>
        <span data-testid={`game-card-score-${game.id}`} className="font-mono text-xs text-zinc-500">
          {STATUS_LABEL[game.status] || game.status}
          {isScored && (
            <>
              {" "}
              &middot;{" "}
              {hasSegmentScore
                ? `${segmentScore.away_runs}-${segmentScore.home_runs} (${SEGMENT_SHORT[segment]})`
                : "segment data unavailable"}
            </>
          )}
        </span>
      </div>
      <div className="flex items-center gap-4 font-mono text-xs text-zinc-400">
        <div data-testid={`game-card-ml-${game.id}`} title={hasMlProbabilities ? `${awayName} ${(prediction.away_probability * 100).toFixed(0)}% / ${homeName} ${(prediction.home_probability * 100).toFixed(0)}%` : undefined}>
          ML {mlValue ?? "--"}
        </div>
        <div>RL --</div>
        <div
          data-testid={`game-card-ou-${game.id}`}
          title={ouIsMarket ? `Market total (${total.market_line.sportsbook})` : ouValue ? "EdgeBall projected total \u2014 no market line synced yet" : undefined}
        >
          O/U {ouValue ?? "--"}
          {ouValue && !ouIsMarket && <span className="text-zinc-600">*</span>}
        </div>
      </div>
      <div className="relative flex items-center justify-end gap-3" ref={previewRef}>
        <button
          type="button"
          data-testid={`game-card-edge-preview-toggle-${game.id}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowPreview((prev) => !prev);
          }}
          className="flex items-center gap-1 font-mono text-xs text-zinc-500 transition-colors hover:text-zinc-300"
        >
          Edge -- <CaretDown size={10} className={`transition-transform ${showPreview ? "rotate-180" : ""}`} />
        </button>
        <RatingBadge rating={null} testId={`game-card-rating-${game.id}`} />

        {showPreview && (
          <div
            data-testid={`game-card-edge-preview-${game.id}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className="absolute right-0 top-full z-20 mt-2 w-60 rounded-lg border border-white/10 bg-eb-bg p-3 text-left shadow-xl"
          >
            <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Market Preview</span>
            <div className="mt-2 flex flex-col gap-1.5">
              {previewRows.map((row) => (
                <div key={row.key} data-testid={`game-card-edge-preview-${row.key}-${game.id}`} className="flex items-center justify-between gap-2 font-mono text-[11px] text-zinc-300">
                  <span className="text-zinc-500">{row.label}</span>
                  <span className="text-right">
                    {row.value} <span className="text-zinc-600">&middot; Edge {row.edge}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Link>
  );
};
